/**
 * sw-video-loop.js — PDP video-loop feature block (snippets/sw-video-loop.liquid).
 *
 * Mount: clones the <template> video into the DOM (so reduced-motion users
 * never fetch the mp4) and flips the wrap to .is-playing, which is what
 * actually hides the static poster (CSS class, not the `hidden` DOM
 * property — an element with `hidden` set still renders if any author rule
 * matches it with equal-or-higher specificity than the UA `[hidden]` rule,
 * which is exactly what `.sw-video-loop__poster { display: block; }` was
 * doing: poster stayed visible, stacked on top of the video, motionless).
 *
 * Click-to-expand: every wrap (mounted or poster-only) carries a
 * .sw-video-loop__trigger button. Clicking it opens a single shared
 * lightbox <dialog>-role overlay playing the clip muted/looping at size,
 * with focus trap + Esc + body scroll lock. For reduced-motion visitors the
 * inline loop never mounts, so the lightbox video is the first time the mp4
 * is ever requested — satisfies "video only plays after explicit click".
 */
(function () {
  'use strict';
  if (window.SWVideoLoop) return;
  window.SWVideoLoop = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var lightbox, lightboxVideo, lightboxCaption, lastFocus;

  function mountLoop(wrap) {
    var tpl = wrap.querySelector('[data-sw-video-loop-source]');
    if (!tpl) return;
    tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl);
    tpl.parentNode.removeChild(tpl);
    wrap.classList.add('is-playing');
  }

  function buildLightbox() {
    var overlay = document.createElement('div');
    overlay.className = 'sw-video-lightbox';
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="sw-video-lightbox__panel" role="dialog" aria-modal="true" aria-label="Video">' +
        '<button type="button" class="sw-video-lightbox__close" aria-label="Close">&times;</button>' +
        '<video class="sw-video-lightbox__video" muted loop playsinline></video>' +
        '<p class="sw-video-lightbox__caption"></p>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', function (event) {
      if (event.target === overlay) closeLightbox();
    });
    overlay.querySelector('.sw-video-lightbox__close').addEventListener('click', closeLightbox);
    lightboxVideo = overlay.querySelector('.sw-video-lightbox__video');
    lightboxCaption = overlay.querySelector('.sw-video-lightbox__caption');
    return overlay;
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      closeLightbox();
      return;
    }
    if (event.key === 'Tab') {
      // Only one focusable control in the lightbox — keep focus pinned to it.
      event.preventDefault();
      lightbox.querySelector('.sw-video-lightbox__close').focus();
    }
  }

  function openLightbox(src, caption) {
    if (!src) return;
    if (!lightbox) lightbox = buildLightbox();
    lightboxVideo.src = src;
    lightboxCaption.textContent = caption || '';
    lastFocus = document.activeElement;
    lightbox.hidden = false;
    document.body.classList.add('sw-scroll-lock');
    lightboxVideo.play().catch(function () {});
    lightbox.querySelector('.sw-video-lightbox__close').focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeLightbox() {
    if (!lightbox || lightbox.hidden) return;
    lightboxVideo.pause();
    lightboxVideo.removeAttribute('src');
    lightboxVideo.load();
    lightbox.hidden = true;
    document.body.classList.remove('sw-scroll-lock');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function init(wrap) {
    if (wrap.hasAttribute('data-sw-video-loop-ready')) return;
    wrap.setAttribute('data-sw-video-loop-ready', '');

    if (!reducedMotion) mountLoop(wrap);

    var trigger = wrap.querySelector('.sw-video-loop__trigger');
    if (!trigger) return;
    trigger.addEventListener('click', function () {
      var caption = wrap.querySelector('.sw-video-loop__caption');
      openLightbox(wrap.getAttribute('data-video-src'), caption ? caption.textContent : '');
    });
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-sw-video-loop]').forEach(init);
  }

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  }
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
